import {
  Checkbox,
  Table,
  Tabs,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowRightLeft,
  Building2,
  Calendar,
  FileSpreadsheet,
  MapPin,
  Package,
  Users,
} from 'lucide-react'
import { LookupBar } from '@/components/common/LookupBar'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { DetailField, DetailPane, SectionCard } from '@/components/common/SectionCard'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { dashboardService } from '@/services'
import { useRegionStore } from '@/stores/regionStore'
import type { MovementDocumentItem } from '@/types'
import { formatDateTime } from '@/utils/cn'

const { Text } = Typography

export default function MovementDocumentPage() {
  const regionView = useRegionStore((s) => s.view)
  const [params, setParams] = useSearchParams()
  const document = params.get('document') || ''
  const includeRef = params.get('include_ref') === '1'
  const [input, setInput] = useState(document)
  const [refFlag, setRefFlag] = useState(includeRef)

  const q = useQuery({
    queryKey: ['movement-document', regionView, document, includeRef],
    queryFn: () => dashboardService.getMovementDocument(document, includeRef),
    enabled: Boolean(document),
  })

  const load = (value: string, include = refFlag) => {
    setInput(value)
    const next: Record<string, string> = {}
    if (value) next.document = value
    if (include) next.include_ref = '1'
    setParams(next)
  }

  const header = q.data?.header
  const itemColumns: ColumnsType<MovementDocumentItem> = [
    {
      title: 'Item',
      dataIndex: 'itemNo',
      width: 90,
      render: (v) => v || '—',
    },
    {
      title: 'Material',
      dataIndex: 'materialCode',
      width: 140,
      render: (v) => v || '—',
    },
    {
      title: 'Description',
      dataIndex: 'materialDesc',
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Mat. group',
      key: 'group',
      width: 140,
      render: (_, r) => r.materialGroupName || r.materialGroupCode || '—',
    },
    {
      title: 'Kit',
      dataIndex: 'kitNo',
      width: 100,
      render: (v) => v || '—',
    },
    {
      title: 'Qty (tags)',
      dataIndex: 'qty',
      width: 100,
      align: 'right',
    },
    {
      title: 'Virtual',
      dataIndex: 'virtualCount',
      width: 90,
      align: 'right',
    },
    {
      title: 'Movements',
      dataIndex: 'movementCount',
      width: 110,
      align: 'right',
    },
  ]

  return (
    <PageContainer fluid className="movement-doc-page">
      <PageHeader
        title="Movement Document"
        subtitle="Document-level overview: partners, locations, line items, and tag movements."
      />

      <SectionCard title="Lookup" subtitle="Enter a movement / allotment document number.">
        <LookupBar
          type="document"
          value={input}
          placeholder="Search document number…"
          submitLabel="Display"
          onChange={setInput}
          onSubmit={(v) => load(v)}
          extra={
            <Checkbox
              checked={refFlag}
              onChange={(e) => {
                setRefFlag(e.target.checked)
                if (document) load(document, e.target.checked)
              }}
            >
              Also match <code>ref_doc_num</code>
            </Checkbox>
          }
        />
      </SectionCard>

      {!document ? (
        <PageEmpty description="Enter a document number to display the movement document." />
      ) : q.isLoading ? (
        <PageLoading />
      ) : q.isError ? (
        <PageError message={(q.error as { message?: string })?.message} />
      ) : !header || header.movementCount === 0 ? (
        <PageEmpty description={`No movements found for document ${document}`} />
      ) : (
        <>
            {/* Document hero — SD header feel */}
            <div className="movement-doc-hero">
              <div className="movement-doc-hero__accent" />
              <div className="movement-doc-hero__inner">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      Movement document
                    </div>
                    <h2 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                      {header.document}
                    </h2>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {header.primaryTransferType ? (
                        <span className="movement-doc-chip movement-doc-chip--solid">
                          {header.primaryTransferType}
                        </span>
                      ) : null}
                      {header.transferTypes
                        .filter((t) => t !== header.primaryTransferType)
                        .map((t) => (
                          <span key={t} className="movement-doc-chip">
                            {t}
                          </span>
                        ))}
                      <span className="movement-doc-chip">{header.itemCount} items</span>
                      <span className="movement-doc-chip">{header.tagCount} tags</span>
                      <span className="movement-doc-chip">{header.movementCount} movements</span>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5">
                      <Calendar size={14} />
                      {formatDateTime(header.firstMovementAt)}
                    </div>
                    {header.lastMovementAt && header.lastMovementAt !== header.firstMovementAt ? (
                      <div className="mt-1 text-xs">→ {formatDateTime(header.lastMovementAt)}</div>
                    ) : null}
                    {header.createdBy ? (
                      <div className="mt-2 text-xs">Posted by {header.createdBy}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Header panes */}
            <div className="movement-doc-panes">
              <DetailPane title="Partner data" subtitle="Sender / receiver" icon={Users}>
                <DetailField
                  label="Sender"
                  value={
                    header.senderCode ? (
                      <Link
                        className="leap-link"
                        to={`/party-journey?role=sender&code=${encodeURIComponent(header.senderCode)}`}
                      >
                        {header.senderName || header.senderCode}
                        {header.senderName ? (
                          <span className="ml-1 font-normal text-muted-foreground">
                            ({header.senderCode})
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <DetailField
                  label="Receiver"
                  value={
                    header.receiverCode ? (
                      <Link
                        className="leap-link"
                        to={`/party-journey?role=receiver&code=${encodeURIComponent(header.receiverCode)}`}
                      >
                        {header.receiverName || header.receiverCode}
                        {header.receiverName ? (
                          <span className="ml-1 font-normal text-muted-foreground">
                            ({header.receiverCode})
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <DetailField label="Created by" value={header.createdBy} />
              </DetailPane>

              <DetailPane title="Organizational data" subtitle="Locations" icon={Building2}>
                <DetailField label="Plant" value={header.warehouse} />
                <DetailField label="Site" value={header.customerLocation} />
                <DetailField
                  label="All plants"
                  value={header.warehouses.length ? header.warehouses.join(', ') : '—'}
                />
                <DetailField
                  label="All sites"
                  value={
                    header.customerLocations.length ? header.customerLocations.join(', ') : '—'
                  }
                />
              </DetailPane>

              <DetailPane title="Document data" subtitle="References & types" icon={FileSpreadsheet}>
                <DetailField label="Document" value={header.document} />
                <DetailField
                  label="Reference docs"
                  value={header.refDocNums.length ? header.refDocNums.join(', ') : '—'}
                />
                <DetailField
                  label="Transfer types"
                  value={header.transferTypes.join(', ') || '—'}
                />
                <DetailField
                  label="Include ref match"
                  value={header.includeRef ? 'Yes' : 'No'}
                />
              </DetailPane>

              <DetailPane title="Quantities" subtitle="Derived from RFID tags" icon={Package}>
                <DetailField label="Line items" value={header.itemCount} />
                <DetailField label="Materials" value={header.materialCount} />
                <DetailField label="RFID tags" value={header.tagCount} />
                <DetailField label="Virtual tags" value={header.virtualTagCount} />
                <DetailField label="Movement events" value={header.movementCount} />
              </DetailPane>
            </div>

            {/* Items + overview tabs */}
            <SectionCard
              title="Document items"
              subtitle="Line items grouped by item number and material (qty = distinct tags)"
              count={header.itemCount}
              variant="table"
            >
              <div className="sd-items-table">
                <Table
                  className="leap-table"
                  rowKey={(r) => `${r.itemNo ?? ''}::${r.materialCode ?? ''}`}
                  columns={itemColumns}
                  dataSource={q.data?.items ?? []}
                  size="middle"
                  pagination={false}
                  expandable={{
                    expandedRowRender: (r) => (
                      <div className="flex flex-wrap gap-2 py-1">
                        {r.tags.map((t) => (
                          <Link
                            key={t}
                            to={`/tag-journey?code=${encodeURIComponent(t)}`}
                            className="leap-link rounded-md bg-muted px-2 py-1 text-xs"
                          >
                            {t}
                          </Link>
                        ))}
                      </div>
                    ),
                    rowExpandable: (r) => r.tags.length > 0,
                  }}
                />
              </div>
            </SectionCard>

            <SectionCard title="Document overview">
              <Tabs
                items={[
                  {
                    key: 'movements',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <ArrowRightLeft size={14} /> Movements
                      </span>
                    ),
                    children: <MovementTable rows={q.data?.movements ?? []} />,
                  },
                  {
                    key: 'locations',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={14} /> Transfer mix
                      </span>
                    ),
                    children: (
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {header.byTransferType.map((row) => (
                          <div
                            key={row.transferType}
                            className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3"
                          >
                            <Text strong>{row.transferType}</Text>
                            <span className="text-sm font-semibold text-primary">{row.count}</span>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            </SectionCard>
        </>
      )}
    </PageContainer>
  )
}
